/**
 * Date utilities for consistent date/time formatting across the application
 */

/**
 * Format a date to a localized string with proper timezone handling
 * @param {string|Date} dateString - Date string or Date object to format
 * @param {boolean} includeTime - Whether to include time in the output
 * @returns {string} Formatted date string
 */
export const formatDateTime = (dateString, includeTime = true) => {
  // Safety check - if input is null, undefined or empty, just return empty string
  if (!dateString) return '';
  
  try {
    let date;
    
    // Convert the input to a string if it's not already (handle Date objects, etc.)
    const dateStr = String(dateString);
    
    // Special handling for MySQL format (YYYY-MM-DD HH:MM:SS)
    if (dateStr.includes(' ') && !dateStr.includes('T')) {
      try {
        // MySQL datetime format - parse directly with components to avoid timezone issues
        const parts = dateStr.split(/[- :]/);
        if (parts.length >= 6) {
          // Extract all parts as numbers with fallbacks in case parsing fails
          let year = parseInt(parts[0], 10);
          if (isNaN(year)) year = new Date().getFullYear();
          
          let month = parseInt(parts[1], 10) - 1; // JS months are 0-based
          if (isNaN(month) || month < 0 || month > 11) month = 0;
          
          let day = parseInt(parts[2], 10);
          if (isNaN(day) || day < 1 || day > 31) day = 1;
          
          let hour = parseInt(parts[3], 10);
          if (isNaN(hour) || hour < 0 || hour > 23) hour = 0;
          
          let minute = parseInt(parts[4], 10);
          if (isNaN(minute) || minute < 0 || minute > 59) minute = 0;
          
          let second = parseInt(parts[5], 10);
          if (isNaN(second) || second < 0 || second > 59) second = 0;
          
          // Check if year is too far in future (probably wrong)
          const currentYear = new Date().getFullYear();
          const correctedYear = (year > currentYear + 1) ? currentYear : year;
          
          // Create date with explicit components to ensure correct local interpretation
          date = new Date(correctedYear, month, day, hour, minute, second);
          
          // Extra safety check
          if (isNaN(date.getTime())) {
            throw new Error('Invalid date after component parsing');
          }
        } else {
          // If parsing fails, fall back to standard method
          const mysqlFormat = dateStr.replace(' ', 'T');
          date = new Date(mysqlFormat);
        }
      } catch (parseError) {
        // If special parsing fails, try standard date parsing
        console.warn('MySQL date parsing failed, falling back:', parseError);
        date = new Date(dateStr);
      }
    } else {
      // Standard date parsing for other formats
      date = new Date(dateStr);
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date: ${dateStr}`);
      
      // Last resort - use current date if we can't parse
      date = new Date();
      
      // If that also fails, just return the original
      if (isNaN(date.getTime())) {
        return dateStr;
      }
    }
    
    // Use locale options with explicit timezone enforcement
    const options = includeTime
      ? { 
          year: 'numeric', 
          month: 'numeric', 
          day: 'numeric', 
          hour: '2-digit', 
          minute: '2-digit', 
          second: '2-digit',
          hour12: true  // Use 12-hour format with AM/PM
        }
      : { year: 'numeric', month: 'numeric', day: 'numeric' };
    
    try {
      // Use en-US locale for consistent MM/DD/YYYY formatting across the app
      return date.toLocaleString('en-US', options);
    } catch (localeError) {
      // If toLocaleString fails, try a simple formatting fallback
      console.warn('toLocaleString failed:', localeError);
      
      const pad = (num) => String(num).padStart(2, '0');
      if (includeTime) {
        // Force MM/DD/YYYY format instead of DD/MM/YYYY
        return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}, ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
      } else {
        return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
      }
    }
  } catch (error) {
    console.error(`Error formatting date ${dateString}:`, error);
    // Return a safe fallback value
    return String(dateString || '');
  }
};

/**
 * Format a date to a short format (e.g., "19/5/2025")
 * @param {string|Date} dateString - Date string or Date object to format
 * @returns {string} Formatted date string
 */
export const formatShortDate = (dateString) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date: ${dateString}`);
      return dateString; // Return original string if invalid
    }
    
    const options = { year: 'numeric', month: 'numeric', day: 'numeric' };
    return date.toLocaleDateString(undefined, options);
  } catch (error) {
    console.error(`Error formatting date ${dateString}:`, error);
    return dateString; // Return original string on error
  }
};

/**
 * Format a date to a time-only format (e.g., "10:30 AM")
 * @param {string|Date} dateString - Date string or Date object to format
 * @returns {string} Formatted time string
 */
export const formatTime = (dateString) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date: ${dateString}`);
      return dateString; // Return original string if invalid
    }
    
    const options = { hour: '2-digit', minute: '2-digit', second: '2-digit' };
    return date.toLocaleTimeString(undefined, options);
  } catch (error) {
    console.error(`Error formatting time ${dateString}:`, error);
    return dateString; // Return original string on error
  }
};

/**
 * Calculate the duration between two dates
 * @param {string|Date} startDate - Start date
 * @param {string|Date} endDate - End date 
 * @returns {string} Formatted duration string
 */
export const calculateDuration = (startDate, endDate) => {
  if (!startDate || !endDate) return 'In Progress';
  
  try {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Check if dates are valid
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      console.warn(`Invalid dates for duration calculation: ${startDate} - ${endDate}`);
      return 'Invalid duration';
    }
    
    const diff = Math.abs(end - start);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h ${minutes}m`;
  } catch (error) {
    console.error(`Error calculating duration:`, error);
    return 'Error';
  }
};

/**
 * Format date for MySQL (YYYY-MM-DD)
 * @param {string|Date} dateString - Date to format
 * @returns {string} MySQL formatted date
 */
export const formatMySQLDate = (dateString) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date: ${dateString}`);
      return dateString; // Return original string if invalid
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error(`Error formatting MySQL date ${dateString}:`, error);
    return dateString; // Return original string on error
  }
};

/**
 * Format date for MySQL datetime (YYYY-MM-DD HH:MM:SS)
 * @param {string|Date} dateString - Date to format
 * @returns {string} MySQL formatted datetime
 */
export const formatMySQLDateTime = (dateString) => {
  if (!dateString) return '';
  
  try {
    const date = dateString instanceof Date ? dateString : new Date(dateString);
    // const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date: ${dateString}`);
      return dateString; // Return original string if invalid
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    console.error(`Error formatting MySQL datetime ${dateString}:`, error);
    return dateString; // Return original string on error
  }
};

export default {
  formatDateTime,
  formatShortDate,
  formatTime,
  calculateDuration,
  formatMySQLDate,
  formatMySQLDateTime
}; 